import { FirebaseError } from "firebase/app";
import {
  AuthErrorCodes,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  signInWithPopup
} from "firebase/auth";

import useGetFirebaseAuth from "../hooks/useGetFirebaseAuth";
import errorTranslator from "../helpers/errorTranslator";
import { AuthProvider, AuthStatus } from "../types";
import providers from "../providers";
import attemptFirstMISLogin from "../utils/attemptFirstMISLogin";

function credentialFromError(authProvider: AuthProvider, error: unknown) {
  return authProvider === "google"
    ? GoogleAuthProvider.credentialFromError(error as FirebaseError)
    : OAuthProvider.credentialFromError(error as FirebaseError);
}

export default function useGetAuthMethods() {
  const auth = useGetFirebaseAuth();

  async function signInWithSSO(authProvider: AuthProvider) {
    try {
      const provider = providers[authProvider].setCustomParameters({
        prompt: "select_account",
      });

      const response = await signInWithPopup(auth, provider);

      return {
        status: AuthStatus.success,
        result: {
          user: response,
          credential: OAuthProvider.credentialFromResult(response)
        }
      };
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === AuthErrorCodes.NEED_CONFIRMATION) {
          const email = (error.customData as { email?: string } | undefined)
            ?.email;
          const credential = credentialFromError(authProvider, error);

          if (email && credential) {
            try {
              const userCredential = await attemptFirstMISLogin({
                auth,
                credential,
                email
              });

              return {
                status: AuthStatus.success,
                result: { user: userCredential, credential }
              };
            } catch {
              // Fall through to standard error translation below.
            }
          }
        }

        return {
          status: AuthStatus.error,
          result: {
            error: {
              code: error.code as string,
              message: errorTranslator(error.code),
            },
          },
        };
      }

      throw error;
    }
  }

  async function signOut() {
    await firebaseSignOut(auth);
  }

  return { signInWithSSO, signOut };
}
